/**
 * Style Guide Page (Admin Only)
 *
 * Internal reference page for checking MoneyMojo styles applied from
 * index.css and tailwind.config.js. Not customer-facing.
 */

import 'remixicon/fonts/remixicon.css';
import React, { useState, useEffect, useMemo } from 'react';
import { Download, Plus, Trash2, Heart, AtSign, Loader2, ChevronDown } from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';

type Section = 'brand' | 'colors' | 'typography' | 'buttons' | 'icons' | 'components' | 'layouts';

const SECTIONS: { id: Section; label: string; icon: string; iconClass?: string }[] = [
  { id: 'brand', label: 'Brand Guidelines', icon: '◇', iconClass: 'ri-bookmark-line' },
  { id: 'colors', label: 'Colors', icon: '◉' },
  { id: 'typography', label: 'Typography', icon: 'T' },
  { id: 'buttons', label: 'Buttons', icon: '▢', iconClass: 'ri-cursor-line' },
  { id: 'icons', label: 'Icons', icon: '♥' },
  { id: 'components', label: 'Components', icon: '■', iconClass: 'ri-layout-4-line' },
  { id: 'layouts', label: 'Layouts', icon: '⊞', iconClass: 'ri-layout-line' },
];

interface IconItem {
  class: string;
  name: string;
  description: string;
}

const ICON_CATEGORIES: { title: string; icons: IconItem[] }[] = [
  {
    title: 'Financial',
    icons: [
      { class: 'ri-wallet-3-line', name: 'wallet-3-line', description: 'Wallet, balance, main money icon' },
      { class: 'ri-money-dollar-circle-line', name: 'money-dollar-circle-line', description: 'Income, salary, revenue' },
      { class: 'ri-bank-line', name: 'bank-line', description: 'Banking, financial institutions' },
      { class: 'ri-pie-chart-line', name: 'pie-chart-line', description: 'Budget breakdown, analytics' },
      { class: 'ri-line-chart-line', name: 'line-chart-line', description: 'Growth, trends, performance' },
      { class: 'ri-calculator-line', name: 'calculator-line', description: 'Calculations, budgeting tools' },
      { class: 'ri-safe-line', name: 'safe-line', description: 'Security, savings, protection' },
      { class: 'ri-exchange-line', name: 'exchange-line', description: 'Currency exchange, transfers' },
    ],
  },
  {
    title: 'Actions',
    icons: [
      { class: 'ri-add-line', name: 'add-line', description: 'Add new items, create' },
      { class: 'ri-delete-bin-line', name: 'delete-bin-line', description: 'Delete, remove items' },
      { class: 'ri-edit-line', name: 'edit-line', description: 'Edit, modify content' },
      { class: 'ri-save-line', name: 'save-line', description: 'Save, confirm actions' },
      { class: 'ri-download-line', name: 'download-line', description: 'Download, export data' },
      { class: 'ri-upload-line', name: 'upload-line', description: 'Upload, import data' },
      { class: 'ri-refresh-line', name: 'refresh-line', description: 'Refresh, reload data' },
      { class: 'ri-search-line', name: 'search-line', description: 'Search functionality' },
    ],
  },
  {
    title: 'Navigation',
    icons: [
      { class: 'ri-arrow-left-line', name: 'arrow-left-line', description: 'Back, previous page' },
      { class: 'ri-arrow-right-line', name: 'arrow-right-line', description: 'Forward, next page' },
      { class: 'ri-arrow-up-line', name: 'arrow-up-line', description: 'Up, increase, positive' },
      { class: 'ri-arrow-down-line', name: 'arrow-down-line', description: 'Down, decrease, negative' },
      { class: 'ri-close-line', name: 'close-line', description: 'Close modals, cancel' },
      { class: 'ri-menu-line', name: 'menu-line', description: 'Menu toggle, hamburger' },
      { class: 'ri-home-line', name: 'home-line', description: 'Home, dashboard' },
      { class: 'ri-external-link-line', name: 'external-link-line', description: 'External links' },
    ],
  },
  {
    title: 'Status & Alerts',
    icons: [
      { class: 'ri-check-line', name: 'check-line', description: 'Success, completion' },
      { class: 'ri-alert-line', name: 'alert-line', description: 'Warnings, alerts' },
      { class: 'ri-error-warning-line', name: 'error-warning-line', description: 'Errors, problems' },
      { class: 'ri-information-line', name: 'information-line', description: 'Information, help' },
      { class: 'ri-shield-check-line', name: 'shield-check-line', description: 'Security, protection' },
      { class: 'ri-notification-3-line', name: 'notification-3-line', description: 'Notifications, alerts' },
      { class: 'ri-question-line', name: 'question-line', description: 'Help, unknown status' },
      { class: 'ri-time-line', name: 'time-line', description: 'Time, pending status' },
    ],
  },
  {
    title: 'Categories',
    icons: [
      { class: 'ri-shopping-cart-line', name: 'shopping-cart-line', description: 'Shopping expenses' },
      { class: 'ri-restaurant-line', name: 'restaurant-line', description: 'Food & dining' },
      { class: 'ri-car-line', name: 'car-line', description: 'Transportation, vehicles' },
      { class: 'ri-home-4-line', name: 'home-4-line', description: 'Housing, rent, utilities' },
      { class: 'ri-heart-line', name: 'heart-line', description: 'Healthcare, medical' },
      { class: 'ri-movie-line', name: 'movie-line', description: 'Entertainment' },
      { class: 'ri-graduation-cap-line', name: 'graduation-cap-line', description: 'Education, learning' },
      { class: 'ri-gift-line', name: 'gift-line', description: 'Gifts, donations' },
    ],
  },
  {
    title: 'UI Elements',
    icons: [
      { class: 'ri-settings-3-line', name: 'settings-3-line', description: 'Settings, configuration' },
      { class: 'ri-user-line', name: 'user-line', description: 'User profile, account' },
      { class: 'ri-calendar-line', name: 'calendar-line', description: 'Dates, scheduling' },
      { class: 'ri-filter-line', name: 'filter-line', description: 'Filtering, sorting' },
      { class: 'ri-eye-line', name: 'eye-line', description: 'View, visibility' },
      { class: 'ri-eye-off-line', name: 'eye-off-line', description: 'Hide, privacy' },
      { class: 'ri-lock-line', name: 'lock-line', description: 'Security, private' },
      { class: 'ri-lock-unlock-line', name: 'lock-unlock-line', description: 'Unlocked, public' },
    ],
  },
];

const PRIMARY_COLORS = [
  { name: 'Primary 50', hex: '#eff6ff', class: 'bg-primary-50', usage: 'Light backgrounds, subtle highlights' },
  { name: 'Primary 100', hex: '#dbeafe', class: 'bg-primary-100', usage: 'Category badges, light accents' },
  { name: 'Primary 600', hex: '#2563eb', class: 'bg-primary-600', usage: 'Primary buttons, brand color' },
  { name: 'Primary 700', hex: '#1d4ed8', class: 'bg-primary-700', usage: 'Button hover states' },
];

const SEMANTIC_COLORS = [
  { name: 'Success 50', hex: '#f0fdf4', class: 'bg-success-50', usage: 'Success backgrounds' },
  { name: 'Success 600', hex: '#16a34a', class: 'bg-success-600', usage: 'Success states, positive values' },
  { name: 'Error 50', hex: '#fef2f2', class: 'bg-error-50', usage: 'Error backgrounds' },
  { name: 'Error 600', hex: '#dc2626', class: 'bg-error-600', usage: 'Error states, negative values' },
  { name: 'Warning 50', hex: '#fffbeb', class: 'bg-warning-50', usage: 'Warning backgrounds' },
  { name: 'Warning 600', hex: '#ca8a04', class: 'bg-warning-600', usage: 'Warning states, pending items' },
  { name: 'Secondary 50', hex: '#faf5ff', class: 'bg-secondary-50', usage: 'Special feature backgrounds' },
  { name: 'Secondary 600', hex: '#9333ea', class: 'bg-secondary-600', usage: 'Secondary actions, transfers' },
];

const NEUTRAL_COLORS = [
  { name: 'Gray 50', hex: '#f9fafb', class: 'bg-gray-50', usage: 'Page backgrounds' },
  { name: 'Gray 100', hex: '#f3f4f6', class: 'bg-gray-100', usage: 'Card backgrounds, dividers' },
  { name: 'Gray 200', hex: '#e5e7eb', class: 'bg-gray-200', usage: 'Borders, inactive states' },
  { name: 'Gray 600', hex: '#4b5563', class: 'bg-gray-600', usage: 'Secondary text' },
  { name: 'Gray 900', hex: '#111827', class: 'bg-gray-900', usage: 'Primary text, headings' },
  { name: 'White', hex: '#ffffff', class: 'bg-white', usage: 'Card backgrounds, main content' },
];

const BUTTON_CUSTOM_COLORS = [
  { name: 'btn-primary', hex: '#2563eb', usage: 'Normal state' },
  { name: 'btn-primary hover', hex: '#2a5be0', usage: 'Hover state' },
  { name: 'btn-secondary', hex: '#9333ea', usage: 'Normal state' },
  { name: 'btn-secondary hover', hex: '#7d3fa5', usage: 'Hover state' },
];

function BrandGuidelinesSection(): React.ReactElement {
  return (
    <section aria-labelledby="brand-heading">
      <h2 id="brand-heading" className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        Brand Guidelines
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        Our brand represents trust, security, and financial empowerment. These guidelines ensure consistent
        application of our visual identity across all touchpoints.
      </p>

      <div className="space-y-10">
        {/* Brand Identity */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Brand Identity</h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Our Mission</p>
              <p className="text-gray-700 dark:text-gray-300">
                Money Mojo empowers individuals to take control of their financial future through intuitive, secure,
                and privacy-focused money management tools that work entirely on their device.
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Brand Values</p>
              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  { title: 'Privacy First', desc: 'Your financial data stored to SOC standards' },
                  { title: 'Trust & Security', desc: 'Bank-level security without the complexity' },
                  { title: 'Simplicity', desc: 'Financial management made accessible to everyone' },
                  { title: 'Empowerment', desc: 'Tools that help you make better financial decisions' },
                ].map(({ title, desc }) => (
                  <div key={title} className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                    <p className="font-medium text-gray-900 dark:text-gray-100">{title}</p>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <p className="font-brand text-3xl font-bold text-gray-900 dark:text-gray-100">Money Mojo</p>
              <p className="mt-1 font-brand text-base text-gray-600 dark:text-gray-400">
                Welcome to your financial future
              </p>
            </div>
          </div>
        </div>

        {/* Logo Usage */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Logo Usage</h3>
          <div className="space-y-4">
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
                <p className="font-brand text-2xl font-bold text-primary-600">Money Mojo</p>
                <p className="mt-2 text-xs text-gray-500">Use this version on light backgrounds.</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-900 p-6 dark:border-gray-600">
                <p className="font-brand text-2xl font-bold text-white">Money Mojo</p>
                <p className="mt-2 text-xs text-gray-400">Use white version on dark backgrounds.</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Size Examples</p>
              <div className="flex flex-wrap items-end gap-6">
                {[
                  { label: 'Large (32px+)', size: 'text-3xl' },
                  { label: 'Medium (24px)', size: 'text-2xl' },
                  { label: 'Small (18px)', size: 'text-lg' },
                  { label: 'Minimum (16px)', size: 'text-base' },
                ].map(({ label, size }) => (
                  <div key={label} className="flex flex-col items-center gap-1">
                    <p className={`font-brand font-bold text-primary-600 ${size}`}>Money Mojo</p>
                    <span className="text-xs text-gray-500">{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="rounded-lg border border-success-200 bg-success-50 p-4 dark:border-success-800 dark:bg-success-950/30">
                <h4 className="font-medium text-success-800 dark:text-success-200 mb-2">Do</h4>
                <ul className="list-inside list-disc space-y-1 text-sm text-success-700 dark:text-success-300">
                  <li>Always maintain clear space around logo equal to the height of the &quot;M&quot;</li>
                  <li>Use Pacifico font exclusively for the brand name</li>
                  <li>Ensure minimum size is 16px height for readability</li>
                  <li>Use primary blue (#2563eb) on light backgrounds</li>
                  <li>Scale proportionally when resizing</li>
                  <li>Place on high-contrast backgrounds for visibility</li>
                </ul>
              </div>
              <div className="rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-800 dark:bg-error-950/30">
                <h4 className="font-medium text-error-800 dark:text-error-200 mb-2">Don&apos;t</h4>
                <ul className="list-inside list-disc space-y-1 text-sm text-error-700 dark:text-error-300">
                  <li>Stretch, skew, or distort the logo</li>
                  <li>Use different fonts for the brand name</li>
                  <li>Place on low-contrast backgrounds</li>
                  <li>Add drop shadows, outlines, or effects</li>
                  <li>Use below minimum size (16px)</li>
                  <li>Crowd with other elements</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Color Principles */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Color Principles</h3>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Primary Brand Colors</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-primary-600" />
                    <div>
                      <p className="font-medium">Primary Blue #2563eb</p>
                      <p className="text-xs text-gray-500">Trust, security, reliability</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-primary-50" />
                    <div>
                      <p className="font-medium">Light Blue #eff6ff</p>
                      <p className="text-xs text-gray-500">Backgrounds, highlights</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Supporting Colors</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-success-600" />
                    <div>
                      <p className="font-medium">Success Green #16a34a</p>
                      <p className="text-xs text-gray-500">Growth, positive outcomes</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-gray-900" />
                    <div>
                      <p className="font-medium">Text Gray #111827</p>
                      <p className="text-xs text-gray-500">Primary text content</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-primary-200 bg-primary-50 p-4 dark:border-primary-800 dark:bg-primary-950/30">
                <p className="font-medium text-primary-800 dark:text-primary-200">Trust & Security (Blue)</p>
                <p className="mt-1 text-sm text-primary-700 dark:text-primary-300">
                  Blue conveys reliability and professional financial services.
                </p>
              </div>
              <div className="rounded-lg border border-success-200 bg-success-50 p-4 dark:border-success-800 dark:bg-success-950/30">
                <p className="font-medium text-success-800 dark:text-success-200">Growth & Success (Green)</p>
                <p className="mt-1 text-sm text-success-700 dark:text-success-300">
                  Green represents financial growth and positive outcomes.
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                <p className="font-medium text-gray-800 dark:text-gray-200">Clarity & Focus (Light)</p>
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                  Light backgrounds ensure readability and reduce eye strain.
                </p>
              </div>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="rounded-lg border border-success-200 bg-success-50 p-4 dark:border-success-800 dark:bg-success-950/30">
                <h4 className="font-medium text-success-800 dark:text-success-200 mb-2">Color Usage Do</h4>
                <ul className="list-inside list-disc space-y-1 text-sm text-success-700 dark:text-success-300">
                  <li>Use blue for primary actions and brand elements</li>
                  <li>Apply green for positive financial indicators</li>
                  <li>Maintain high contrast ratios (4.5:1 minimum)</li>
                  <li>Use light backgrounds for content areas</li>
                  <li>Ensure colors convey meaning consistently</li>
                  <li>Test accessibility with colourblind users</li>
                </ul>
              </div>
              <div className="rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-800 dark:bg-error-950/30">
                <h4 className="font-medium text-error-800 dark:text-error-200 mb-2">Color Usage Avoid</h4>
                <ul className="list-inside list-disc space-y-1 text-sm text-error-700 dark:text-error-300">
                  <li>Using red for positive financial values</li>
                  <li>Creating custom colors outside the palette</li>
                  <li>Low contrast color combinations</li>
                  <li>Overusing bright colors in large areas</li>
                  <li>Color as the only way to convey information</li>
                  <li>Dark backgrounds for primary content</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Typography Rules */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Typography Rules</h3>
          <div className="space-y-6">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Pacifico (Brand Font)</p>
              <p className="font-brand text-2xl text-gray-900 dark:text-gray-100">
                Money Mojo Welcome to your future
              </p>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Use exclusively for brand name and special headings. Avoid: Never for body text or UI elements.
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Use cases: Brand name in navigation, hero section headings, special promotional text.
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Roboto (Primary Font)</p>
              <p className="text-base text-gray-900 dark:text-gray-100">
                Dashboard Overview — Your financial summary for this month shows steady growth across all categories.
                Last updated: January 15, 2024
              </p>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Use for all UI elements, body text, and headings. Use cases: All interface text, page headings and
                subheadings, body content and descriptions, navigation menus.
              </p>
            </div>
          </div>
        </div>

        {/* Typography Hierarchy */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Typography Hierarchy</h3>
          <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
            {[
              { level: 'Page Title (H1)', class: 'text-3xl font-bold', sample: 'Page Title' },
              { level: 'Section Heading (H2)', class: 'text-2xl font-bold', sample: 'Section Heading' },
              { level: 'Card Heading (H3)', class: 'text-xl font-semibold', sample: 'Card Heading' },
              { level: 'Body Text', class: 'text-base', sample: 'Body text for main content' },
              { level: 'Secondary Text', class: 'text-sm text-gray-600', sample: 'Supporting information' },
              { level: 'Caption Text', class: 'text-xs text-gray-500', sample: 'Labels and fine print' },
            ].map(({ level, class: cls, sample }) => (
              <div key={level}>
                <p className={cls}>{sample}</p>
                <p className="mt-0.5 text-xs text-gray-500">{level} — {cls}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Typography Best Practices */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Typography Best Practices</h3>
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="rounded-lg border border-success-200 bg-success-50 p-4 dark:border-success-800 dark:bg-success-950/30">
              <h4 className="font-medium text-success-800 dark:text-success-200 mb-2">Do</h4>
              <ul className="list-inside list-disc space-y-1 text-sm text-success-700 dark:text-success-300">
                <li>Maintain consistent font sizes across similar elements</li>
                <li>Use proper heading hierarchy (H1 → H2 → H3)</li>
                <li>Ensure sufficient line spacing for readability</li>
                <li>Use Pacifico sparingly for brand impact</li>
                <li>Keep line lengths between 45–75 characters</li>
                <li>Use appropriate font weights for emphasis</li>
              </ul>
            </div>
            <div className="rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-800 dark:bg-error-950/30">
              <h4 className="font-medium text-error-800 dark:text-error-200 mb-2">Don&apos;t</h4>
              <ul className="list-inside list-disc space-y-1 text-sm text-error-700 dark:text-error-300">
                <li>Use Pacifico for body text or UI elements</li>
                <li>Mix too many font weights in one section</li>
                <li>Use text smaller than 12px for important content</li>
                <li>Skip heading levels in hierarchy</li>
                <li>Use all caps for large blocks of text</li>
                <li>Use insufficient contrast between text and background</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Brand in Action */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Brand in Action</h3>
          <div className="rounded-xl bg-gradient-to-r from-primary-600 to-secondary-600 p-6">
            <div className="grid gap-6 sm:grid-cols-3">
              <div className="rounded-lg bg-white/10 p-4 backdrop-blur-sm">
                <i className="ri-checkbox-circle-line ri-2x text-white mb-2 block" aria-hidden />
                <p className="font-medium text-white">Consistent Visual Identity</p>
                <p className="mt-1 text-sm text-white/90">
                  Every touchpoint reflects our commitment to clarity, trust, and financial empowerment.
                </p>
              </div>
              <div className="rounded-lg bg-white/10 p-4 backdrop-blur-sm">
                <i className="ri-shield-check-line ri-2x text-white mb-2 block" aria-hidden />
                <p className="font-medium text-white">Trust Through Design</p>
                <p className="mt-1 text-sm text-white/90">
                  Our blue-focused palette and clean typography build confidence in financial decisions.
                </p>
              </div>
              <div className="rounded-lg bg-white/10 p-4 backdrop-blur-sm">
                <i className="ri-user-settings-line ri-2x text-white mb-2 block" aria-hidden />
                <p className="font-medium text-white">User-Centred Approach</p>
                <p className="mt-1 text-sm text-white/90">
                  Every design choice prioritises clarity, accessibility, and ease of use for all users.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function IconsSection(): React.ReactElement {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredCategories = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return ICON_CATEGORIES;
    return ICON_CATEGORIES.map((cat) => ({
      ...cat,
      icons: cat.icons.filter(
        (icon) =>
          icon.name.toLowerCase().includes(q) ||
          icon.description.toLowerCase().includes(q)
      ),
    })).filter((cat) => cat.icons.length > 0);
  }, [searchQuery]);

  return (
    <section aria-labelledby="icons-heading">
      <h2 id="icons-heading" className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        Icon Library
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        We use Remix Icon library for consistent, high-quality icons throughout the application. All icons follow
        the same visual style and sizing principles.
      </p>

      {/* Search */}
      <div className="mb-8">
        <div className="relative max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" aria-hidden />
          <input
            type="search"
            placeholder="Search icons..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
            aria-label="Search icons"
          />
        </div>
      </div>

      {/* Icon Categories */}
      <div className="space-y-8">
        {filteredCategories.map((category) => (
          <div key={category.title}>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{category.title}</h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {category.icons.map((icon) => (
                <div
                  key={icon.class}
                  className="flex items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50"
                >
                  <i className={`${icon.class} ri-lg text-gray-700 dark:text-gray-300`} aria-hidden />
                  <div className="min-w-0">
                    <code className="text-xs text-gray-600 dark:text-gray-400 break-all">{icon.name}</code>
                    <p className="text-sm text-gray-500 dark:text-gray-500">{icon.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {filteredCategories.length === 0 && (
          <p className="text-gray-500 dark:text-gray-500">No icons match your search.</p>
        )}
      </div>

      {/* Icon Sizes */}
      <div className="mt-10">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Icon Sizes</h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { size: 'Small', class: 'ri-sm', usage: 'UI elements, buttons' },
            { size: 'Medium', class: 'ri-1x', usage: 'Default size, most common' },
            { size: 'Large', class: 'ri-lg', usage: 'Headings, emphasis' },
            { size: 'Extra Large', class: 'ri-xl', usage: 'Feature highlights' },
          ].map(({ size, class: sizeClass, usage }) => (
            <div
              key={size}
              className="flex flex-col items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50"
            >
              <i className={`ri-heart-line ${sizeClass} text-gray-700 dark:text-gray-300`} aria-hidden />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{size}</span>
              <code className="text-xs text-gray-500 dark:text-gray-500">{sizeClass}</code>
              <span className="text-xs text-gray-500 dark:text-gray-500 text-center">{usage}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Usage Guidelines */}
      <div className="mt-10">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Usage Guidelines</h3>
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="rounded-lg border border-success-200 bg-success-50 p-4 dark:border-success-800 dark:bg-success-950/30">
            <h4 className="font-medium text-success-800 dark:text-success-200 mb-2">Do</h4>
            <ul className="list-inside list-disc space-y-1 text-sm text-success-700 dark:text-success-300">
              <li>Use consistent icon sizes throughout</li>
              <li>Center icons in parent elements</li>
              <li>Use semantic colors (blue for actions, red for danger)</li>
              <li>Choose icons that clearly represent their function</li>
              <li>Use filled versions for active/selected states</li>
            </ul>
          </div>
          <div className="rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-800 dark:bg-error-950/30">
            <h4 className="font-medium text-error-800 dark:text-error-200 mb-2">Don&apos;t</h4>
            <ul className="list-inside list-disc space-y-1 text-sm text-error-700 dark:text-error-300">
              <li>Mix different icon styles</li>
              <li>Use similar icons for different functions</li>
              <li>Make icons too small to recognise</li>
              <li>Use decorative icons without purpose</li>
              <li>Forget to make interactive icons clickable</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Implementation Example */}
      <div className="mt-10">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Implementation Example</h3>
        <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">HTML / JSX structure</p>
            <pre className="overflow-x-auto rounded bg-gray-900 p-4 text-sm text-gray-100">
              <code>{`<i className="ri-heart-line ri-lg" aria-hidden></i>`}</code>
            </pre>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">With button</p>
            <pre className="overflow-x-auto rounded bg-gray-900 p-4 text-sm text-gray-100">
              <code>{`<button className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg">
  <i className="ri-download-line ri-1x" aria-hidden></i>
  Download
</button>`}</code>
            </pre>
          </div>
        </div>
      </div>
    </section>
  );
}

export function StyleGuidePage(): React.ReactElement {
  const [activeSection, setActiveSection] = useState<Section>('brand');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const activeSectionData = SECTIONS.find((s) => s.id === activeSection) ?? SECTIONS[0];

  const handleSectionSelect = (section: Section) => {
    setActiveSection(section);
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    if (isDropdownOpen) {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setIsDropdownOpen(false);
      };
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isDropdownOpen]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Style Guide"
        description="Internal reference for MoneyMojo design system. Not customer-facing."
        headingLevel="h1"
      />

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left Sidebar Navigation */}
        <aside className="w-full lg:w-64 flex-shrink-0">
          <nav
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4"
            aria-label="Style guide sections"
          >
            {/* Desktop - Sidebar list */}
            <div className="hidden lg:block space-y-1">
              {SECTIONS.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    activeSection === section.id
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                  aria-current={activeSection === section.id ? 'page' : undefined}
                >
                  {section.iconClass ? (
                    <i className={`${section.iconClass} ri-lg`} aria-hidden />
                  ) : (
                    <span className="text-lg" aria-hidden>
                      {section.icon}
                    </span>
                  )}
                  {section.label}
                </button>
              ))}
            </div>

            {/* Mobile/Tablet - Dropdown */}
            <div className="lg:hidden relative">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                aria-label="Select style guide section"
                aria-expanded={isDropdownOpen}
                aria-haspopup="listbox"
              >
                <div className="flex items-center gap-3">
                  {activeSectionData.iconClass ? (
                    <i className={`${activeSectionData.iconClass} ri-lg`} aria-hidden />
                  ) : (
                    <span className="text-lg" aria-hidden>
                      {activeSectionData.icon}
                    </span>
                  )}
                  <span className="text-sm font-medium">{activeSectionData.label}</span>
                </div>
                <ChevronDown
                  className={`w-5 h-5 flex-shrink-0 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>

              {isDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsDropdownOpen(false)}
                    aria-hidden
                  />
                  <ul
                    className="absolute z-20 w-full mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                    role="listbox"
                  >
                    {SECTIONS.map((section) => (
                      <li key={section.id} role="option" aria-selected={activeSection === section.id}>
                        <button
                          type="button"
                          onClick={() => handleSectionSelect(section.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            activeSection === section.id
                              ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400 font-semibold'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }`}
                        >
                          {section.iconClass ? (
                            <i className={`${section.iconClass} ri-lg`} aria-hidden />
                          ) : (
                            <span className="text-lg" aria-hidden>
                              {section.icon}
                            </span>
                          )}
                          <span className="text-sm font-medium">{section.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 space-y-10" id="main-content">
          {/* Brand Guidelines */}
          {activeSection === 'brand' && (
            <BrandGuidelinesSection />
          )}

          {/* Colors */}
          {activeSection === 'colors' && (
            <section aria-labelledby="color-palette-heading">
              <h2 id="color-palette-heading" className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Color Palette
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Our color system is designed to convey trust, security, and financial growth while maintaining
                excellent accessibility standards.
              </p>

              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Primary Colors</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {PRIMARY_COLORS.map((color) => (
                      <div
                        key={color.name}
                        className="flex items-center gap-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                      >
                        <div className={`h-12 w-24 shrink-0 rounded-md border border-gray-200 ${color.class}`} />
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 dark:text-gray-100">{color.name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Hex: {color.hex}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-500">Class: {color.class}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{color.usage}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Semantic Colors</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {SEMANTIC_COLORS.map((color) => (
                      <div
                        key={color.name}
                        className="flex items-center gap-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                      >
                        <div className={`h-12 w-24 shrink-0 rounded-md border border-gray-200 ${color.class}`} />
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 dark:text-gray-100">{color.name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Hex: {color.hex}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-500">Class: {color.class}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{color.usage}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Neutral Colors</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {NEUTRAL_COLORS.map((color) => (
                      <div
                        key={color.name}
                        className="flex items-center gap-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                      >
                        <div className={`h-12 w-24 shrink-0 rounded-md border border-gray-200 ${color.class}`} />
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 dark:text-gray-100">{color.name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Hex: {color.hex}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-500">Class: {color.class}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{color.usage}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Button Custom Colors (from index.css)
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    These override Tailwind primary/secondary for buttons.
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {BUTTON_CUSTOM_COLORS.map((color) => (
                      <div
                        key={color.name}
                        className="flex items-center gap-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                      >
                        <div
                          className="h-12 w-24 shrink-0 rounded-md border border-gray-200"
                          style={{ backgroundColor: color.hex }}
                        />
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 dark:text-gray-100">{color.name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Hex: {color.hex}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{color.usage}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Typography */}
          {activeSection === 'typography' && (
            <section aria-labelledby="typography-heading">
              <h2 id="typography-heading" className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Typography
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                Our typography system uses Roboto for body text and UI elements, with Pacifico for brand elements
                to create a modern, trustworthy appearance.
              </p>

              <div className="space-y-10">
                {/* Font Families */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Font Families</h3>

                  <div className="space-y-6">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">Roboto (Primary)</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        Used for headings, body text, and UI elements.
                      </p>
                      <p className="font-sans text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                        The quick brown fox jumps over the lazy dog
                      </p>
                      <p className="font-sans text-sm text-gray-600 dark:text-gray-400 mb-1">
                        ABCDEFGHIJKLMNOPQRSTUVWXYZ
                      </p>
                      <p className="font-sans text-sm text-gray-600 dark:text-gray-400 mb-1">
                        abcdefghijklmnopqrstuvwxyz
                      </p>
                      <p className="font-sans text-sm text-gray-600 dark:text-gray-400">
                        1234567890!@#$%^&*()
                      </p>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">Pacifico (Brand)</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        Used exclusively for the brand name and special headings.
                      </p>
                      <p className="font-brand text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                        Money Mojo
                      </p>
                      <p className="font-brand text-base text-gray-600 dark:text-gray-400">
                        Welcome to your financial future
                      </p>
                    </div>
                  </div>
                </div>

                {/* Heading Scale */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Heading Scale</h3>
                  <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                    <div>
                      <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">Page Title (H1)</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">text-3xl font-bold</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">Section Heading (H2)</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">text-2xl font-bold</p>
                    </div>
                    <div>
                      <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">Card Heading (H3)</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">text-xl font-semibold</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">Component Heading (H4)</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">text-lg font-semibold</p>
                    </div>
                    <div>
                      <p className="text-base font-medium text-gray-900 dark:text-gray-100">List Item Heading (H5)</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">text-base font-medium</p>
                    </div>
                  </div>
                </div>

                {/* Body Text */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Body Text</h3>
                  <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                    <div>
                      <p className="text-base text-gray-900 dark:text-gray-100">
                        Regular body text for main content and descriptions.
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">text-base text-gray-900</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Secondary text for supporting information and metadata.
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">text-sm text-gray-600</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        Caption text for labels, timestamps, and fine print.
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">text-xs text-gray-500</p>
                    </div>
                  </div>
                </div>

                {/* Financial Numbers */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Financial Numbers</h3>
                  <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">$12,847.50</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Dashboard stats</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-success-600">+$2,350.00</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Transaction amount (positive)</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-error-600">-$125.99</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Transaction amount (negative)</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-900 dark:text-gray-100">$45.32</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Small amount</p>
                    </div>
                  </div>
                </div>

                {/* Special Text Styles */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Special Text Styles</h3>
                  <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                    <div>
                      <a href="#typography" className="text-primary-600 underline hover:text-primary-700">
                        Interactive link text
                      </a>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Links</p>
                    </div>
                    <div>
                      <p className="text-sm text-success-600">Operation completed successfully</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Success text</p>
                    </div>
                    <div>
                      <p className="text-sm text-error-600">Error message text</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Error text</p>
                    </div>
                    <div>
                      <p className="text-sm text-warning-600">Warning message text</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Warning text</p>
                    </div>
                    <div>
                      <code className="font-mono text-sm rounded bg-gray-200 px-1.5 py-0.5 dark:bg-gray-700">
                        font-family: monospace
                      </code>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Code / monospace</p>
                    </div>
                  </div>
                </div>

                {/* Typography Usage Guidelines */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Usage Guidelines</h3>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div className="rounded-lg border border-success-200 bg-success-50 p-4 dark:border-success-800 dark:bg-success-950/30">
                      <h4 className="font-medium text-success-800 dark:text-success-200 mb-2">Do</h4>
                      <ul className="list-inside list-disc space-y-1 text-sm text-success-700 dark:text-success-300">
                        <li>Use consistent font sizes throughout the app</li>
                        <li>Maintain proper hierarchy with heading levels</li>
                        <li>Use appropriate colors for different text types</li>
                        <li>Ensure sufficient line spacing for readability</li>
                        <li>Use Pacifico sparingly for brand elements only</li>
                      </ul>
                    </div>
                    <div className="rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-800 dark:bg-error-950/30">
                      <h4 className="font-medium text-error-800 dark:text-error-200 mb-2">Don&apos;t</h4>
                      <ul className="list-inside list-disc space-y-1 text-sm text-error-700 dark:text-error-300">
                        <li>Use Pacifico for body text or UI elements</li>
                        <li>Mix too many font weights in one section</li>
                        <li>Use text that&apos;s too small to read comfortably</li>
                        <li>Skip heading levels in the hierarchy</li>
                        <li>Use all caps for large blocks of text</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Buttons */}
          {activeSection === 'buttons' && (
            <section aria-labelledby="buttons-heading">
              <h2 id="buttons-heading" className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Buttons
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                Button components with consistent styling, proper hover states, and accessibility features.
              </p>

              <div className="space-y-10">
                {/* Primary Buttons */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Primary Buttons</h3>
                  <div className="flex flex-wrap items-center gap-4">
                    <button type="button" className="btn btn-primary btn-md whitespace-nowrap cursor-pointer">
                      Primary Button
                    </button>
                    <button type="button" className="btn btn-primary btn-lg whitespace-nowrap cursor-pointer">
                      Large Primary
                    </button>
                    <button type="button" className="btn btn-primary btn-sm whitespace-nowrap cursor-pointer">
                      Small Primary
                    </button>
                  </div>
                  <code className="mt-2 block text-xs text-gray-500 dark:text-gray-500">
                    btn btn-primary btn-md | btn-lg | btn-sm
                  </code>
                </div>

                {/* Secondary Buttons */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Secondary Buttons</h3>
                  <div className="flex flex-wrap items-center gap-4">
                    <button
                      type="button"
                      className="btn btn-md whitespace-nowrap cursor-pointer px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      Secondary Button
                    </button>
                    <button
                      type="button"
                      className="btn btn-lg whitespace-nowrap cursor-pointer px-6 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      Large Secondary
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm whitespace-nowrap cursor-pointer px-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      Small Secondary
                    </button>
                  </div>
                  <code className="mt-2 block text-xs text-gray-500 dark:text-gray-500">
                    bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50
                  </code>
                </div>

                {/* Icon Buttons */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Icon Buttons</h3>
                  <div className="flex flex-wrap gap-4">
                    <button
                      type="button"
                      className="btn btn-primary btn-md inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      <Download size={16} aria-hidden />
                      Download
                    </button>
                    <button
                      type="button"
                      className="btn btn-md inline-flex items-center gap-2 whitespace-nowrap cursor-pointer bg-success-600 text-white hover:bg-success-700 rounded-md"
                    >
                      <Plus size={16} aria-hidden />
                      Add New
                    </button>
                    <button
                      type="button"
                      className="btn btn-destructive btn-md inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      <Trash2 size={16} aria-hidden />
                      Delete
                    </button>
                  </div>
                </div>

                {/* Icon Only Buttons */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Icon Only Buttons</h3>
                  <div className="flex flex-wrap gap-4">
                    <button
                      type="button"
                      className="h-10 w-10 inline-flex items-center justify-center rounded-full border-2 border-primary-600 bg-primary-50 text-primary-600 hover:bg-primary-100 cursor-pointer"
                      aria-label="Favourite"
                    >
                      <Heart size={18} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 cursor-pointer dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                      aria-label="Mention"
                    >
                      <AtSign size={18} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-success-600 text-white hover:bg-success-700 cursor-pointer"
                      aria-label="Add"
                    >
                      <Plus size={18} aria-hidden />
                    </button>
                  </div>
                </div>

                {/* Ghost Buttons */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Ghost Buttons</h3>
                  <div className="flex flex-wrap gap-4">
                    <button type="button" className="btn btn-ghost btn-md text-primary-600 hover:text-primary-700">
                      Ghost Button
                    </button>
                    <button type="button" className="btn btn-ghost btn-md text-gray-600 hover:text-gray-700">
                      Ghost Gray
                    </button>
                    <button type="button" className="btn btn-ghost btn-md text-error-600 hover:text-error-700">
                      Ghost Danger
                    </button>
                  </div>
                </div>

                {/* Button States */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Button States</h3>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Primary</h4>
                      <div className="flex flex-wrap gap-3">
                        <button type="button" className="btn btn-primary btn-md">
                          Button
                        </button>
                        <button type="button" className="btn btn-primary btn-md" data-loading="true">
                          <Loader2 className="animate-spin" size={16} aria-hidden />
                          {' '}Loading...
                        </button>
                        <button type="button" className="btn btn-primary btn-md" disabled>
                          Button
                        </button>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">Normal | Loading | Disabled</p>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Secondary</h4>
                      <div className="flex flex-wrap gap-3">
                        <button type="button" className="btn btn-secondary btn-md">
                          Button
                        </button>
                        <button type="button" className="btn btn-secondary btn-md" data-loading="true">
                          <Loader2 className="animate-spin" size={16} aria-hidden />
                          {' '}Loading...
                        </button>
                        <button type="button" className="btn btn-secondary btn-md" disabled>
                          Button
                        </button>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">Normal | Loading | Disabled</p>
                    </div>
                  </div>
                </div>

                {/* Button Groups */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Button Groups</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Horizontal Group</p>
                      <div className="inline-flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                        <button type="button" className="btn btn-primary btn-md rounded-l-md rounded-r-none cursor-pointer">
                          Save
                        </button>
                        <button
                          type="button"
                          className="btn btn-md rounded-r-md rounded-l-none cursor-pointer bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Segmented Control</p>
                      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-800">
                        {(['Day', 'Week', 'Month'] as const).map((opt, i) => (
                          <button
                            key={opt}
                            type="button"
                            className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap cursor-pointer ${
                              i === 0
                                ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                            }`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Usage Guidelines */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Usage Guidelines</h3>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div className="rounded-lg border border-success-200 bg-success-50 p-4 dark:border-success-800 dark:bg-success-950/30">
                      <h4 className="font-medium text-success-800 dark:text-success-200 mb-2">Do</h4>
                      <ul className="list-inside list-disc space-y-1 text-sm text-success-700 dark:text-success-300">
                        <li>Use primary buttons for main actions</li>
                        <li>Add whitespace-nowrap to prevent text wrapping</li>
                        <li>Include cursor-pointer for interactive elements</li>
                        <li>Use appropriate loading states</li>
                        <li>Maintain consistent padding and sizing</li>
                      </ul>
                    </div>
                    <div className="rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-800 dark:bg-error-950/30">
                      <h4 className="font-medium text-error-800 dark:text-error-200 mb-2">Don&apos;t</h4>
                      <ul className="list-inside list-disc space-y-1 text-sm text-error-700 dark:text-error-300">
                        <li>Use multiple primary buttons in one section</li>
                        <li>Make buttons too small to tap easily</li>
                        <li>Forget disabled states for form validation</li>
                        <li>Use colors that don&apos;t meet accessibility standards</li>
                        <li>Create custom button styles outside the system</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Icons */}
          {activeSection === 'icons' && (
            <IconsSection />
          )}

          {/* Components */}
          {activeSection === 'components' && (
            <section aria-labelledby="components-heading">
              <h2 id="components-heading" className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Components
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                Reusable component patterns and examples used throughout the MoneyMojo application.
              </p>

              <div className="space-y-10">
                {/* Cards */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Cards</h3>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Basic Card</p>
                      <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm transition-shadow hover:shadow-lg dark:border-gray-700 dark:bg-gray-800">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Basic Card</h4>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          Card description text goes here with supporting details.
                        </p>
                        <div className="mt-4">
                          <button type="button" className="btn btn-primary btn-sm">
                            Action Button
                          </button>
                        </div>
                      </div>
                      <code className="mt-2 block text-xs text-gray-500">
                        bg-white rounded-xl border border-gray-100 p-6 hover:shadow-lg transition-shadow
                      </code>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Stat Card</p>
                      <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm transition-shadow hover:shadow-lg dark:border-gray-700 dark:bg-gray-800">
                        <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">Current Balance</h4>
                        <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">$12,847.50</p>
                        <p className="mt-2 flex items-center gap-1 text-sm text-success-600">
                          <i className="ri-line-chart-line ri-sm" aria-hidden />
                          +2.5% from last month
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Elements */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Form Elements</h3>
                  <div className="space-y-6">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">Text Inputs</p>
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                          <label className="label mb-1" htmlFor="comp-hint">Label, Status & Hint</label>
                          <input id="comp-hint" type="text" className="input" placeholder="Hint text." />
                        </div>
                        <div>
                          <label className="label mb-1" htmlFor="comp-active">Active</label>
                          <input id="comp-active" type="text" className="input ring-2 ring-primary-600 ring-offset-0" placeholder="Placeholder" />
                        </div>
                        <div>
                          <label className="label mb-1 text-error-600" htmlFor="comp-error">Error</label>
                          <input id="comp-error" type="text" className="input border-error-600 ring-2 ring-error-600 ring-offset-0" defaultValue="0000" />
                          <p className="mt-1 text-xs text-error-600">Error Message Here</p>
                        </div>
                        <div>
                          <label className="label mb-1 text-warning-600" htmlFor="comp-warning">Warning</label>
                          <input id="comp-warning" type="text" className="input border-warning-500 ring-2 ring-warning-500 ring-offset-0" defaultValue="0000" />
                          <p className="mt-1 text-xs text-warning-600">Error Message Here</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="label mb-1" htmlFor="comp-textarea">Textarea</label>
                      <textarea id="comp-textarea" className="input min-h-[80px]" placeholder="Enter description..." rows={3} />
                    </div>
                    <div>
                      <label className="label mb-1" htmlFor="comp-select">Select Dropdown</label>
                      <select id="comp-select" className="input input-select">
                        <option value="">Choose an option</option>
                        <option value="1">Option 1</option>
                        <option value="2">Option 2</option>
                      </select>
                    </div>
                    <div className="flex flex-wrap gap-6">
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Toggle Switches</p>
                        <div className="flex items-center gap-4">
                          <label className="relative inline-flex cursor-pointer items-center">
                            <input type="checkbox" defaultChecked className="peer sr-only" />
                            <div className="h-6 w-11 rounded-full bg-primary-600 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-5" />
                            <span className="ml-2 text-sm">Toggle ON</span>
                          </label>
                          <label className="relative inline-flex cursor-pointer items-center">
                            <input type="checkbox" className="peer sr-only" />
                            <div className="h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all dark:bg-gray-600" />
                            <span className="ml-2 text-sm">Toggle OFF</span>
                          </label>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Radio Buttons</p>
                        <div className="flex gap-4">
                          {['Radio Button', 'Option 2', 'Option 3'].map((opt, i) => (
                            <label key={opt} className="flex cursor-pointer items-center gap-2">
                              <input type="radio" name="comp-radio" defaultChecked={i === 0} className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500" />
                              <span className="text-sm">{opt}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Checkboxes</p>
                        <div className="flex gap-4">
                          {['Checkbox', 'Option 2', 'Option 3'].map((opt, i) => (
                            <label key={opt} className="flex cursor-pointer items-center gap-2">
                              <input type="checkbox" defaultChecked={i === 0} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                              <span className="text-sm">{opt}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                    <code className="block text-xs text-gray-500">
                      focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent
                    </code>
                  </div>
                </div>

                {/* Badges and Tags */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Badges and Tags</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Status Badges</p>
                      <div className="flex flex-wrap gap-2">
                        <span className="rounded-full bg-success-100 px-2.5 py-0.5 text-xs font-medium text-success-800 dark:bg-success-900/30 dark:text-success-300">
                          Completed
                        </span>
                        <span className="rounded-full bg-warning-100 px-2.5 py-0.5 text-xs font-medium text-warning-800 dark:bg-warning-900/30 dark:text-warning-300">
                          Pending
                        </span>
                        <span className="rounded-full bg-error-100 px-2.5 py-0.5 text-xs font-medium text-error-800 dark:bg-error-900/30 dark:text-error-300">
                          Failed
                        </span>
                        <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-800 dark:bg-secondary-900/30 dark:text-secondary-300">
                          Processing
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Category Tags</p>
                      <div className="flex flex-wrap gap-2">
                        {['Food & Dining', 'Transportation', 'Entertainment'].map((tag) => (
                          <span
                            key={tag}
                            className="rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lists */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Lists</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Simple List</p>
                      <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
                        {['List Item 1', 'List Item 2'].map((item) => (
                          <li key={item}>
                            <button type="button" className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50">
                              {item}
                              <i className="ri-arrow-right-s-line text-gray-500" aria-hidden />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Transaction List Item</p>
                      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
                              <i className="ri-cup-line ri-lg text-gray-600 dark:text-gray-400" aria-hidden />
                            </span>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-gray-100">Starbucks Coffee</p>
                              <p className="text-xs text-gray-500 dark:text-gray-500">Food & Dining · Jan 15</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="rounded-full bg-success-100 px-2 py-0.5 text-xs font-medium text-success-800 dark:bg-success-900/30 dark:text-success-300">
                              Completed
                            </span>
                            <span className="font-medium text-error-600">-$8.75</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Structure */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Modal Structure</h3>
                  <div className="relative min-h-[280px] rounded-xl bg-gray-200 dark:bg-gray-700">
                    <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/50 p-4">
                      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Modal Title</h4>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          Modal content goes here with proper spacing.
                        </p>
                        <div className="mt-6 flex justify-end gap-2">
                          <button type="button" className="btn btn-ghost btn-md">
                            Cancel
                          </button>
                          <button type="button" className="btn btn-primary btn-md">
                            Confirm
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <code className="mt-4 block text-xs text-gray-500">
                    Overlay: fixed inset-0 bg-black/50 z-50
                  </code>
                </div>

                {/* Notifications */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Notifications</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 rounded-lg border border-success-200 bg-success-50 p-4 dark:border-success-800 dark:bg-success-950/30">
                      <i className="ri-check-line ri-xl text-success-600 flex-shrink-0" aria-hidden />
                      <p className="text-sm font-medium text-success-800 dark:text-success-200">
                        Success! Your transaction has been processed successfully.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-800 dark:bg-error-950/30">
                      <i className="ri-close-circle-line ri-xl text-error-600 flex-shrink-0" aria-hidden />
                      <p className="text-sm font-medium text-error-800 dark:text-error-200">
                        Error: There was a problem processing your request.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 rounded-lg border border-warning-200 bg-warning-50 p-4 dark:border-warning-800 dark:bg-warning-950/30">
                      <i className="ri-alert-line ri-xl text-warning-600 flex-shrink-0" aria-hidden />
                      <p className="text-sm font-medium text-warning-800 dark:text-warning-200">
                        Warning: You have exceeded 90% of your budget this month.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Component Guidelines */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Component Guidelines</h3>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div className="rounded-lg border border-success-200 bg-success-50 p-4 dark:border-success-800 dark:bg-success-950/30">
                      <h4 className="font-medium text-success-800 dark:text-success-200 mb-2">Do</h4>
                      <ul className="list-inside list-disc space-y-1 text-sm text-success-700 dark:text-success-300">
                        <li>Use consistent spacing and padding</li>
                        <li>Add hover and focus states</li>
                        <li>Use semantic HTML</li>
                        <li>Follow visual hierarchy</li>
                      </ul>
                    </div>
                    <div className="rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-800 dark:bg-error-950/30">
                      <h4 className="font-medium text-error-800 dark:text-error-200 mb-2">Don&apos;t</h4>
                      <ul className="list-inside list-disc space-y-1 text-sm text-error-700 dark:text-error-300">
                        <li>Create custom components without design approval</li>
                        <li>Mix border radius values</li>
                        <li>Use inconsistent shadows</li>
                        <li>Forget loading and error states</li>
                        <li>Ignore responsive design principles</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Layouts */}
          {activeSection === 'layouts' && (
            <section aria-labelledby="layouts-heading">
              <h2 id="layouts-heading" className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Layout Patterns
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                Common layout patterns and grid systems used throughout the MoneyMojo application for consistent
                spacing and alignment.
              </p>

              <div className="space-y-10">
                {/* Grid Systems */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Grid Systems</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">2-Column Grid</p>
                      <div className="grid grid-cols-1 gap-6 rounded-lg border border-gray-200 bg-gray-50 p-4 lg:grid-cols-2 dark:border-gray-700 dark:bg-gray-800/50">
                        <div className="rounded-md border border-gray-200 bg-white py-4 text-center text-sm dark:border-gray-600 dark:bg-gray-800">
                          Column 1
                        </div>
                        <div className="rounded-md border border-gray-200 bg-white py-4 text-center text-sm dark:border-gray-600 dark:bg-gray-800">
                          Column 2
                        </div>
                      </div>
                      <code className="mt-2 block text-xs text-gray-500">grid grid-cols-1 lg:grid-cols-2 gap-6</code>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">3-Column Grid</p>
                      <div className="grid grid-cols-1 gap-6 rounded-lg border border-gray-200 bg-gray-50 p-4 md:grid-cols-2 lg:grid-cols-3 dark:border-gray-700 dark:bg-gray-800/50">
                        <div className="rounded-md border border-gray-200 bg-white py-4 text-center text-sm dark:border-gray-600 dark:bg-gray-800">
                          Column 1
                        </div>
                        <div className="rounded-md border border-gray-200 bg-white py-4 text-center text-sm dark:border-gray-600 dark:bg-gray-800">
                          Column 2
                        </div>
                        <div className="rounded-md border border-gray-200 bg-white py-4 text-center text-sm dark:border-gray-600 dark:bg-gray-800">
                          Column 3
                        </div>
                      </div>
                      <code className="mt-2 block text-xs text-gray-500">
                        grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6
                      </code>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">4-Column Grid</p>
                      <div className="grid grid-cols-1 gap-6 rounded-lg border border-gray-200 bg-gray-50 p-4 md:grid-cols-2 lg:grid-cols-4 dark:border-gray-700 dark:bg-gray-800/50">
                        {['Col 1', 'Col 2', 'Col 3', 'Col 4'].map((col) => (
                          <div
                            key={col}
                            className="rounded-md border border-gray-200 bg-white py-4 text-center text-sm dark:border-gray-600 dark:bg-gray-800"
                          >
                            {col}
                          </div>
                        ))}
                      </div>
                      <code className="mt-2 block text-xs text-gray-500">grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6</code>
                    </div>
                  </div>
                </div>

                {/* Sidebar Layouts */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Sidebar Layouts</h3>
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Left Sidebar</p>
                    <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 lg:flex-row dark:border-gray-700 dark:bg-gray-800/50">
                      <aside className="w-full shrink-0 rounded-md border border-gray-200 bg-white p-4 lg:w-64 dark:border-gray-600 dark:bg-gray-800">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Sidebar Navigation</p>
                        {['Menu Item 1', 'Menu Item 2', 'Menu Item 3'].map((item) => (
                          <p key={item} className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                            {item}
                          </p>
                        ))}
                      </aside>
                      <main className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white p-4 dark:border-gray-600 dark:bg-gray-800">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Main Content Area</p>
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-500">
                          Primary content goes here with flexible width.
                        </p>
                      </main>
                    </div>
                    <code className="mt-2 block text-xs text-gray-500">
                      flex flex-col lg:flex-row gap-6 | lg:w-64 flex-shrink-0 | lg:flex-1
                    </code>
                  </div>
                </div>

                {/* Card Layouts */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Card Layouts</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Dashboard Stats Layout</p>
                      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                        {[1, 2, 3, 4].map((i) => (
                          <div
                            key={i}
                            className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-600 dark:bg-primary-900/30">
                              <i className="ri-line-chart-line" aria-hidden />
                            </span>
                            <p className="mt-2 text-sm text-success-600">+2.5%</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">$12,847</p>
                            <p className="text-xs text-gray-500 dark:text-gray-500">Current Balance</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Feature Cards</p>
                      <div className="grid gap-6 lg:grid-cols-3">
                        <div className="rounded-xl border border-gray-200 bg-white p-6 lg:col-span-2 dark:border-gray-700 dark:bg-gray-800">
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Chart Component</p>
                          <div className="mt-4 h-24 rounded-lg bg-gray-100 dark:bg-gray-700" />
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">List Section</p>
                          <ul className="mt-4 space-y-2">
                            <li className="text-sm text-gray-700 dark:text-gray-300">Transaction 1 -$25.00</li>
                            <li className="text-sm text-gray-700 dark:text-gray-300">Transaction 2 -$45.00</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Spacing System */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Spacing System</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Standard Spacing Values</p>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                        <table className="w-full text-sm">
                          <tbody>
                            {[
                              ['2px', 'space-x-0.5', 'gap-0.5'],
                              ['4px', 'space-x-1', 'gap-1'],
                              ['8px', 'space-x-2', 'gap-2'],
                              ['12px', 'space-x-3', 'gap-3'],
                              ['16px', 'space-x-4', 'gap-4'],
                              ['24px', 'space-x-6', 'gap-6'],
                              ['32px', 'space-x-8', 'gap-8'],
                            ].map(([px, space, gap]) => (
                              <tr key={px} className="border-b border-gray-200 last:border-0 dark:border-gray-600">
                                <td className="py-2 font-medium">{px}</td>
                                <td className="py-2 text-gray-600 dark:text-gray-400">{space}</td>
                                <td className="py-2 text-gray-600 dark:text-gray-400">{gap}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Visual Spacing Levels</p>
                      <div className="flex flex-wrap items-end gap-4">
                        {[8, 16, 24, 32, 40, 56, 72, 80, 96, 120].map((px) => (
                          <div key={px} className="flex flex-col items-center gap-1">
                            <div
                              className="rounded bg-gray-300 dark:bg-gray-600"
                              style={{ width: px, height: px }}
                            />
                            <span className="text-xs text-gray-500">{px}px</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Container Sizes */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Container Sizes</h3>
                  <div className="space-y-4">
                    <div className="max-w-7xl mx-auto rounded-lg border-2 border-primary-500 bg-primary-50/50 p-4 dark:bg-primary-900/10">
                      <p className="font-medium text-gray-900 dark:text-gray-100">Main application container</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 (1280px max width)</p>
                    </div>
                    <div className="max-w-4xl mx-auto rounded-lg border-2 border-success-500 bg-success-50/50 p-4 dark:bg-success-900/10">
                      <p className="font-medium text-gray-900 dark:text-gray-100">Content container</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">max-w-4xl mx-auto (896px max width)</p>
                    </div>
                    <div className="max-w-md mx-auto rounded-lg border-2 border-error-500 bg-error-50/50 p-4 dark:bg-error-900/10">
                      <p className="font-medium text-gray-900 dark:text-gray-100">Modal container</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">max-w-md mx-auto (448px max width)</p>
                    </div>
                  </div>
                </div>

                {/* Responsive Breakpoints */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Responsive Breakpoints</h3>
                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-800">
                          <th className="px-4 py-3 text-left font-medium">Breakpoint</th>
                          <th className="px-4 py-3 text-left font-medium">Class prefix</th>
                          <th className="px-4 py-3 text-left font-medium">Min width</th>
                          <th className="px-4 py-3 text-left font-medium">Usage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ['Mobile', '(default)', '0px', 'Base styles'],
                          ['Tablet', 'md:', '768px', '2-column layouts'],
                          ['Desktop', 'lg:', '1024px', '3+ column layouts, sidebars'],
                          ['Large Desktop', 'xl:', '1280px', 'Wide layouts'],
                        ].map(([breakpoint, prefix, width, usage]) => (
                          <tr key={breakpoint} className="border-b border-gray-200 last:border-0 dark:border-gray-600">
                            <td className="px-4 py-3 font-medium">{breakpoint}</td>
                            <td className="px-4 py-3 font-mono text-primary-600 dark:text-primary-400">{prefix}</td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{width}</td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{usage}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {[
                      { label: 'Desktop HD', size: '1440', cols: '12', colW: '65px', gutter: '30px' },
                      { label: 'Desktop', size: '1024', cols: '12', colW: '50px', gutter: '30px' },
                      { label: 'Tablet', size: '768', cols: '8', colW: '88px', gutter: '30px' },
                      { label: 'Mobile', size: '320', cols: '2', colW: '130px', gutter: '30px' },
                    ].map(({ label, size, cols, colW, gutter }) => (
                      <div
                        key={label}
                        className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50"
                      >
                        <p className="font-medium text-gray-900 dark:text-gray-100">{label} {size}</p>
                        <p className="mt-1 text-xs text-gray-500">
                          {cols} columns · {colW} col · {gutter} gutter
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Layout Guidelines */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Layout Guidelines</h3>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div className="rounded-lg border border-success-200 bg-success-50 p-4 dark:border-success-800 dark:bg-success-950/30">
                      <h4 className="font-medium text-success-800 dark:text-success-200 mb-2">Do</h4>
                      <ul className="list-inside list-disc space-y-1 text-sm text-success-700 dark:text-success-300">
                        <li>Use consistent grid gaps (4, 6, 8)</li>
                        <li>Follow mobile-first responsive design</li>
                        <li>Maintain proper content hierarchy</li>
                        <li>Use standard container max-widths</li>
                        <li>Ensure minimum touch target sizes (44px)</li>
                      </ul>
                    </div>
                    <div className="rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-800 dark:bg-error-950/30">
                      <h4 className="font-medium text-error-800 dark:text-error-200 mb-2">Don&apos;t</h4>
                      <ul className="list-inside list-disc space-y-1 text-sm text-error-700 dark:text-error-300">
                        <li>Create custom grid systems without approval</li>
                        <li>Use inconsistent spacing values</li>
                        <li>Ignore responsive breakpoints</li>
                        <li>Make layouts too dense or cluttered</li>
                        <li>Mix different layout patterns randomly</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

        </main>
      </div>
    </div>
  );
}
